import { FilesetResolver, PoseLandmarker, type PoseLandmarkerResult } from '@mediapipe/tasks-vision'
import { type Landmark } from './PoseToRig'
import { type PoseProvider } from './PoseProvider'

/**
 * Pose-detection provider backed by `@mediapipe/tasks-vision` (the official
 * MediaPipe Tasks Vision runtime). The only shipping implementation of
 * `PoseProvider` — see `PoseProvider.ts` for context on why.
 */

const WASM_BASE_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm'
const MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task'

export class MediaPipePoseProvider implements PoseProvider {
  private landmarker: PoseLandmarker | null = null
  private last_video_time: number = -1

  async init (): Promise<void> {
    if (this.landmarker !== null) return // idempotent
    const vision = await FilesetResolver.forVisionTasks(WASM_BASE_URL)
    this.landmarker = await PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL,
        delegate: 'GPU'
      },
      runningMode: 'VIDEO',
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5
    })
  }

  async detect (video: HTMLVideoElement): Promise<Landmark[] | null> {
    if (this.landmarker === null) return null
    if (video.readyState < 2) return null

    // detectForVideo expects a strictly increasing timestamp. Skip duplicate
    // frames (when the video element hasn't advanced).
    const video_time = video.currentTime
    if (video_time === this.last_video_time) return null
    this.last_video_time = video_time

    try {
      const result: PoseLandmarkerResult = this.landmarker.detectForVideo(video, performance.now())
      const world_landmarks = result.worldLandmarks?.[0] as Landmark[] | undefined
      if (world_landmarks === undefined || world_landmarks.length === 0) return null
      return world_landmarks
    } catch (err) {
      console.warn('MediaPipePoseProvider.detect failed (will keep trying):', err)
      return null
    }
  }

  async dispose (): Promise<void> {
    if (this.landmarker === null) return
    try {
      await this.landmarker.close()
    } catch (_) { /* swallow — release is best-effort */ }
    this.landmarker = null
    this.last_video_time = -1
  }
}
